/**
 * conflictSensitivityEngine.js
 *
 * Detects active or latent PASTORAL CONFLICT signals.
 * NOT a risk detector (ethics handles danger).
 * NOT an emotion detector (emotionalSensitivityDetector handles conflict emotion).
 *
 * This detects CONFLICT AS PASTORAL COMPLEXITY:
 *   — relational fractures that need gentle navigation
 *   — doctrinal disagreements requiring prudence
 *   — leadership tensions requiring careful handling
 *   — latent conflict that hasn't surfaced yet
 *
 * When conflict is detected, Kevia should:
 * — increase prudence
 * — reduce simplification
 * — avoid giving sides
 * — resist premature resolution
 *
 * Types:
 *   doctrinal     — disagreement over doctrine or practice
 *   relacional    — interpersonal hurt or fracture
 *   liderazgo     — conflict within or about leadership
 *   latente       — unstated but present tension
 *   ninguno       — no conflict signal
 *
 * Intensities:
 *   latent    — implied but not stated
 *   emerging  — becoming visible
 *   active    — explicitly described
 */

const TYPE_PATTERNS = [
  {
    type: "liderazgo",
    patterns: [
      /(?:conflicto|problema|tensión|desacuerdo|discusión)\s+(?:con|en|entre)\s+(?:los\s+)?(?:ancianos?|diáconos?|líderes?|junta|directiva)/i,
      /(?:el\s+)?liderazgo\s+(?:no\s+me\s+apoya|se\s+opone|está\s+(?:dividido|en\s+contra))/i,
      /(?:los\s+)?(?:ancianos?|líderes?)\s+(?:no\s+(?:confían?|aceptan?|están?\s+de\s+acuerdo)|cuestionan?)\s+(?:mi|mis|el\s+pastor)/i,
    ],
  },
  {
    type: "doctrinal",
    patterns: [
      /desacuerdo\s+doctrinal|conflicto\s+(?:de\s+)?doctrina|diferencias?\s+teológicas?/i,
      /(?:no\s+(?:están?|se)\s+(?:de\s+acuerdo|alineados?)|discrepan?)\s+(?:en\s+)?(?:doctrina|teología|cómo\s+interpretar)/i,
      /(?:interpretación|postura)\s+(?:diferente|contraria|opuesta)\s+(?:sobre|de)\s+(?:la\s+)?(?:biblia|escritura|texto)/i,
    ],
  },
  {
    type: "relacional",
    patterns: [
      /(?:herida|fractura|ruptura)\s+relacional/i,
      /(?:se\s+(?:ofendió|ofendieron?|lastimó|lastimaron?|hirió|hirieron?))\s+(?:con|por)/i,
      /resentimiento\s+(?:entre|de|hacia)/i,
      /(?:no\s+(?:se\s+hablan?|se\s+llevan?|tienen?\s+buena\s+relación))\s+(?:con|entre|desde)/i,
      /relación\s+(?:deteriorada|dañada|rota)\s+(?:con|entre)/i,
    ],
  },
  {
    type: "latente",
    patterns: [
      /(?:se\s+note|se\s+siente|se\s+percibe|hay)\s+(?:algo|una\s+tensión|un\s+ambiente)\s+(?:raro|tenso|diferente|incómodo)/i,
      /algo\s+(?:no\s+(?:está\s+bien|anda\s+bien)|pasa)\s+(?:pero\s+nadie|aunque\s+no)\s+(?:lo\s+dice|lo\s+habla|lo\s+menciona)/i,
      /(?:no\s+(?:dicen|verbalizan?|expresan?))\s+(?:el\s+problema|la\s+tensión|el\s+conflicto)\s+(?:directamente|abiertamente)/i,
    ],
  },
];

/**
 * @param {{
 *   text:    string,
 *   emotion: object|null,
 *   intent:  string,
 *   ethics:  object|null,
 * }} params
 * @returns {{
 *   type:        "doctrinal"|"relacional"|"liderazgo"|"latente"|"ninguno",
 *   intensity:   "latent"|"emerging"|"active",
 *   instruction: string|null,
 * }}
 */
export function detectConflictSignals({ text, emotion, intent, ethics }) {
  const t = text ?? "";

  // Don't fire if ethics already flagged this as a danger-level conflict
  // (that layer handles the high-risk version; we only handle pastoral complexity)
  const ethicsDetected = ethics?.detected ?? [];
  const isEthicsConflict = ethicsDetected.some((d) =>
    ["abuse", "marital_crisis", "discipline"].includes(d)
  );

  let matchedType = null;
  for (const { type, patterns } of TYPE_PATTERNS) {
    if (patterns.some((p) => p.test(t))) {
      matchedType = type;
      break;
    }
  }

  // Emotion-based fallback: explicit conflict emotion → emerging intensity
  if (!matchedType && emotion?.dominantEmotion === "conflict" && !isEthicsConflict) {
    return {
      type:        "relacional",
      intensity:   "emerging",
      instruction: _instruction("relacional", "emerging"),
    };
  }

  if (!matchedType) {
    return { type: "ninguno", intensity: "latent", instruction: null };
  }

  // Determine intensity
  const intensity =
    matchedType === "latente"              ? "latent"   :
    /urgente|crisis|inmediatamente/i.test(t) ? "active"   :
    "emerging";

  return {
    type: matchedType,
    intensity,
    instruction: _instruction(matchedType, intensity),
  };
}

function _instruction(type, intensity) {
  if (type === "liderazgo") {
    return "Conflicto en el liderazgo. No tomes partido. Ayuda a discernir con prudencia — las decisiones aquí afectan a toda la comunidad.";
  }
  if (type === "doctrinal") {
    return "Desacuerdo doctrinal presente. Ofrece claridad sin rigidez, con amor y sin declarar ganador.";
  }
  if (type === "relacional") {
    return intensity === "active"
      ? "Conflicto relacional activo. Antes de proponer pasos, escucha sin simplificar la complejidad del daño."
      : "Hay señales de tensión relacional. No precipites resolución — escucha primero.";
  }
  if (type === "latente") {
    return "Hay tensión no verbalizada. No la fuerzas a la superficie prematuramente — haz preguntas suaves.";
  }
  return null;
}
